import {Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {AddJutePattern} from '@application/command/add-jute-pattern';
import {isMultipleOfValidator} from '@application/validators/is-multiple-of-validator';
import {Pattern} from '@domain/pattern';
import {JuteInformation} from '@domain/production-schedule/jute-information';
import {JuteInformationForPart} from '@domain/production-schedule/jute-information-for-part';
import {JutePosition} from '@domain/production-schedule/jute-position.enum';
import {MachineQualityForProductionSchedule} from '@domain/production-schedule/machine-quality-for-production-schedule';
import {MachineSummary} from '@domain/production-schedule/machine-summary';
import {PathLabelInformation} from '@domain/production-schedule/path-label-information';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription as ProfileSubscription} from '@domain/profile/subscription';
import {Authentication, AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {PatternType} from '@presentation/components/select-pattern/pattern-type.enum';
import {SelectPatternComponentMatDialogConfigDataInterface} from '@presentation/components/select-pattern/select-pattern-component-mat-dialog-config.interface';
import {SelectPatternComponent} from '@presentation/components/select-pattern/select-pattern.component';
import {OverviewListColoredYarnSet} from '@presentation/pages/textile-data/colored-yarn-set/overview/overview-list-colored-yarn-set';
import {WeaveQualityType} from '@presentation/pages/textile-data/machine-quality/overview/weave-quality-type.enum';
import {AssertionUtils, BaseComponent, canShowErrorForErrorCodeAndFormControlForFormGroup, DialogBuilderFactoryService, FormValueHelper, TranslateService} from '@vdw/angular-component-library';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-production-schedule-jute-options',
  templateUrl: './production-schedule-jute-options.component.html',
  styleUrls: ['./production-schedule-jute-options.component.scss']
})
export class ProductionScheduleJuteOptionsComponent extends BaseComponent implements OnChanges {
  @Input() public coloredYarnSet: OverviewListColoredYarnSet;
  @Input() public machineQuality: MachineQualityForProductionSchedule;
  @Input() public pathLabelInformation: PathLabelInformation;
  @Input() public juteInformation: JuteInformation;
  @Input() public factorForConversionFromPicksToMillimeters: number;
  @Input() public factorForConversionFromMillimetersToPicks: number;
  @Input() public machine: MachineSummary;
  @Input() public needsToShowValidationFeedback: boolean;
  @Input() public weaveQualityType: WeaveQualityType;
  @Input() public areAllFieldsDisabled: boolean = false;
  @Output() public juteZoneLoaded = new EventEmitter<void>();

  public readonly JUTE_PATTERN_CONDITIONAL_CHIP_VALUE_KEY = this.translate.instant('PRODUCTION_ORDER.WEAVE_PLAN.JUTE.PATTERN_NAME', {patternId: '%(id)'});

  public juteForm: FormGroup<{
    hasJuteBefore: FormControl<boolean>;
    juteBefore: FormGroup<{
      position: FormGroup<{
        beforePathLabel: FormControl<boolean>;
        afterPathLabel: FormControl<boolean>;
      }>;
      commercialHeightInMM: FormControl<number>;
      technicalHeightInPicks: FormControl<number>;
    }>;
    hasJuteAfter: FormControl<boolean>;
    juteAfter: FormGroup<{
      sameAsJuteBefore: FormControl<boolean>;
      position: FormGroup<{
        beforePathLabel: FormControl<boolean>;
        afterPathLabel: FormControl<boolean>;
      }>;
      commercialHeightInMM: FormControl<number>;
      technicalHeightInPicks: FormControl<number>;
    }>;
    pattern: FormControl<Pattern>;
  }>;

  private currentSubscription: ProfileSubscription;
  private readonly ADD_JUTE_PERMISSION = Permission.ADD_JUTE;

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    private readonly translate: TranslateService,
    private readonly formBuilder: FormBuilder,
    private readonly addJutePattern: AddJutePattern,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService
  ) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (AssertionUtils.isNullOrUndefined(this.currentSubscription)) {
      this.currentSubscription = this.authentication.getCurrentSubscription();

      if (!this.hasPermissionToAddJute()) {
        this.juteZoneLoaded.emit();
      }
    }

    if ('juteInformation' in changes) {
      if (changes.juteInformation.firstChange) {
        this.setFormFields();
      }
      this.setFormValues();
    }

    const hasJuteBefore = !AssertionUtils.isNullOrUndefined(this.juteInformation?.juteBefore);
    const hasJuteAfter = !AssertionUtils.isNullOrUndefined(this.juteInformation?.juteAfter);

    if (!hasJuteAfter && !hasJuteBefore) {
      this.juteZoneLoaded.emit();
    }
  }

  public hasPermissionToAddJute(): boolean {
    return this.currentSubscription?.hasPermission(this.ADD_JUTE_PERMISSION);
  }

  public hasChosenJutePattern(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.juteForm.value.pattern);
  }

  public selectPatternForJute(): void {
    const colorSetId = this.coloredYarnSet.overviewListColorSet.id;

    this.dialogBuilderFactoryService
      .getBuilder()
      .openSelectGridDialog<SelectPatternComponentMatDialogConfigDataInterface<Pattern>, Pattern>(SelectPatternComponent, {
        patternType: PatternType.JUTE_PATTERNS,
        addPatternCommand: this.addJutePattern.execute({machineQualityId: this.machineQuality.id, colorSetId}),
        patternTranslationKey: 'PRODUCTION_ORDER.WEAVE_PLAN.PATTERN_OR_DRAWING',
        allowPatternCreation: true,
        weaveStructureId: this.machineQuality.weaveStructure.id,
        machineQualityId: this.machineQuality.id,
        colorSetId
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((pattern: Pattern | undefined) => {
        if (pattern !== undefined) {
          this.juteForm.controls.pattern.setValue(pattern);
          this.juteZoneLoaded.emit();
        }
      });
  }

  public getMultipleValue(): number {
    if (this.machineQuality.weaveStructure.sendRepeat !== this.machineQuality.weaveStructure.picksPerColorLine) {
      return this.machineQuality.weaveStructure.sendRepeat * this.machineQuality.weaveStructure.picksPerColorLine;
    } else {
      return this.machineQuality.weaveStructure.sendRepeat;
    }
  }

  public getJuteInformation(): JuteInformation {
    let juteBefore: JuteInformationForPart = null;
    let juteAfter: JuteInformationForPart = null;
    let result: JuteInformation = null;
    const pattern: Pattern = this.juteForm.value.pattern;

    if (this.juteForm.getRawValue().hasJuteBefore) {
      const juteBeforePathControl = this.juteForm.controls.juteBefore;

      juteBefore = new JuteInformationForPart(
        this.getJutePosition(juteBeforePathControl.value.position as {beforePathLabel: boolean; afterPathLabel: boolean}),
        juteBeforePathControl.value.commercialHeightInMM,
        juteBeforePathControl.value.technicalHeightInPicks,
        pattern
      );
    }

    if (this.juteForm.getRawValue().hasJuteAfter) {
      const juteAfterPathControl = this.isJuteAfterPathIsSameAsBefore() ? this.juteForm.controls.juteBefore : this.juteForm.controls.juteAfter;

      juteAfter = new JuteInformationForPart(
        this.getJutePosition(juteAfterPathControl.value.position as {beforePathLabel: boolean; afterPathLabel: boolean}),
        juteAfterPathControl.value.commercialHeightInMM,
        juteAfterPathControl.value.technicalHeightInPicks,
        pattern
      );
    }

    if (!AssertionUtils.isNullOrUndefined(juteBefore) || !AssertionUtils.isNullOrUndefined(juteAfter)) {
      result = new JuteInformation(juteBefore, juteAfter);
    }

    return result;
  }

  public onJuteAfterPathIsSameAsBeforeChange(matCheckboxChange: MatCheckboxChange): void {
    if (matCheckboxChange.checked) {
      this.setJuteAfterSameAsJuteBefore();
    }
  }

  public canShowErrorForFormControl(errorCode: string, formControlName: string): boolean {
    return canShowErrorForErrorCodeAndFormControlForFormGroup(errorCode, formControlName, this.juteForm);
  }

  public canShowMultipleError(formControlName: string): boolean {
    return this.canShowErrorForFormControl('multiple', formControlName) && !this.canShowErrorForFormControl('min', formControlName);
  }

  public juteBeforePathChange(matCheckboxChange: MatCheckboxChange): void {
    if (!matCheckboxChange.checked) {
      this.juteForm.controls.juteBefore.reset({
        position: {
          beforePathLabel: true,
          afterPathLabel: true
        }
      });

      if (this.isJuteAfterPathIsSameAsBefore()) {
        this.juteForm.controls.juteAfter.controls.sameAsJuteBefore.setValue(false);
      }
    }
  }

  public juteAfterPathChange(matCheckboxChange: MatCheckboxChange): void {
    if (matCheckboxChange.checked) {
      if (this.juteForm.getRawValue().hasJuteBefore) {
        this.setJuteAfterSameAsJuteBefore();
      }
    } else {
      this.juteForm.controls.juteAfter.reset({
        position: {
          beforePathLabel: true,
          afterPathLabel: true
        }
      });
    }
  }

  public canShowJutePattern(): boolean {
    return this.juteForm.getRawValue().hasJuteBefore || this.juteForm.getRawValue().hasJuteAfter;
  }

  public canShowAddJuteBeforeOrAfterPathLabels(): boolean {
    return (
      this.machine.canHaveRestAndFreeZonesAndPathLabels() &&
      !AssertionUtils.isNullOrUndefined(this.pathLabelInformation) &&
      (this.pathLabelInformation.hasAddedPathLabelBefore() || this.pathLabelInformation.hasAddedPathLabelAfter())
    );
  }

  private setJuteAfterSameAsJuteBefore(): void {
    this.juteForm.controls.juteAfter.setValue(
      {
        ...this.juteForm.get('juteBefore').value,
        sameAsJuteBefore: true
      },
      {emitEvent: false}
    );
  }

  private setFormFields(): void {
    this.juteForm = this.formBuilder.group({
      hasJuteBefore: this.formBuilder.control({value: false, disabled: !this.hasPermissionToAddJute()}),
      juteBefore: this.formBuilder.group({
        position: this.formBuilder.group({
          beforePathLabel: this.formBuilder.control(true),
          afterPathLabel: this.formBuilder.control(true)
        }),
        commercialHeightInMM: this.formBuilder.control(null, [Validators.required, Validators.min(1)]),
        technicalHeightInPicks: this.formBuilder.control(null, [
          Validators.required,
          isMultipleOfValidator(this.machineQuality.weaveStructure.sendRepeat),
          Validators.min(this.machineQuality.weaveStructure.sendRepeat)
        ])
      }),
      hasJuteAfter: this.formBuilder.control({value: false, disabled: !this.hasPermissionToAddJute()}),
      juteAfter: this.formBuilder.group({
        sameAsJuteBefore: this.formBuilder.control(false),
        position: this.formBuilder.group({
          beforePathLabel: this.formBuilder.control(true),
          afterPathLabel: this.formBuilder.control(true)
        }),
        commercialHeightInMM: this.formBuilder.control(null, [Validators.required, Validators.min(1)]),
        technicalHeightInPicks: this.formBuilder.control(null, [
          Validators.required,
          isMultipleOfValidator(this.machineQuality.weaveStructure.sendRepeat),
          Validators.min(this.machineQuality.weaveStructure.sendRepeat)
        ])
      }),
      pattern: this.formBuilder.control(null, Validators.required)
    });

    if (this.areAllFieldsDisabled) {
      this.juteForm.disable();
    }

    this.subscribeToJuteAfterPath();
  }

  private subscribeToJuteAfterPath(): void {
    this.juteForm.controls.juteBefore.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
      if (this.isJuteAfterPathIsSameAsBefore()) {
        this.setJuteAfterSameAsJuteBefore();
      }
    });
  }

  private getJutePosition(position: {beforePathLabel: boolean; afterPathLabel: boolean}): JutePosition {
    const {beforePathLabel, afterPathLabel} = position;
    let jutePosition: JutePosition;

    if (!AssertionUtils.isNullOrUndefined(this.pathLabelInformation)) {
      if (beforePathLabel && afterPathLabel) {
        jutePosition = JutePosition.BEFORE_AND_AFTER_PATHLABEL;
      } else if (beforePathLabel && !afterPathLabel) {
        jutePosition = JutePosition.BEFORE_PATHLABEL;
      } else if (!beforePathLabel && afterPathLabel) {
        jutePosition = JutePosition.AFTER_PATHLABEL;
      } else {
        jutePosition = JutePosition.NO_PATHLABEL;
      }
    } else {
      jutePosition = JutePosition.NO_PATHLABEL;
    }

    return jutePosition;
  }

  private isJuteAfterPathIsSameAsBefore(): boolean {
    return this.juteForm.controls.juteAfter.value.sameAsJuteBefore;
  }

  private setFormValues(): void {
    let pattern: Pattern;
    let juteBeforePositionBeforePathLabel = false;
    let juteBeforePositionAfterPathLabel = false;
    let juteAfterPositionBeforePathLabel = false;
    let juteAfterPositionAfterPathLabel = false;
    const hasJuteBefore = !AssertionUtils.isNullOrUndefined(this.juteInformation?.juteBefore);
    const hasJuteAfter = !AssertionUtils.isNullOrUndefined(this.juteInformation?.juteAfter);

    if (hasJuteBefore) {
      const {juteBefore} = this.juteInformation;
      pattern = juteBefore.pattern;
      const position = juteBefore.position;

      if (this.machine.canHaveRestAndFreeZonesAndPathLabels()) {
        juteBeforePositionBeforePathLabel = position === JutePosition.BEFORE_PATHLABEL || position === JutePosition.BEFORE_AND_AFTER_PATHLABEL;
        juteBeforePositionAfterPathLabel = position === JutePosition.AFTER_PATHLABEL || position === JutePosition.BEFORE_AND_AFTER_PATHLABEL;
      }

      this.juteForm.patchValue(
        {
          hasJuteBefore,
          juteBefore: {
            position: {
              beforePathLabel: juteBeforePositionBeforePathLabel,
              afterPathLabel: juteBeforePositionAfterPathLabel
            },
            commercialHeightInMM: juteBefore.commercialHeightInMM,
            technicalHeightInPicks: juteBefore.technicalHeightInPicks
          },
          pattern
        },
        {emitEvent: false}
      );
    } else {
      this.juteForm.controls.hasJuteBefore.setValue(false);
    }

    if (hasJuteAfter) {
      const {juteAfter} = this.juteInformation;

      pattern = juteAfter.pattern;
      const position = juteAfter.position;

      if (this.machine.canHaveRestAndFreeZonesAndPathLabels()) {
        juteAfterPositionBeforePathLabel = position === JutePosition.BEFORE_PATHLABEL || position === JutePosition.BEFORE_AND_AFTER_PATHLABEL;
        juteAfterPositionAfterPathLabel = position === JutePosition.AFTER_PATHLABEL || position === JutePosition.BEFORE_AND_AFTER_PATHLABEL;
      }

      const sameAsJuteBefore =
        hasJuteBefore &&
        juteBeforePositionBeforePathLabel === juteAfterPositionBeforePathLabel &&
        juteBeforePositionAfterPathLabel === juteAfterPositionAfterPathLabel &&
        juteAfter.commercialHeightInMM === this.juteInformation.juteBefore.commercialHeightInMM &&
        juteAfter.technicalHeightInPicks === this.juteInformation.juteBefore.technicalHeightInPicks;

      this.juteForm.patchValue(
        {
          hasJuteAfter,
          juteAfter: {
            sameAsJuteBefore,
            position: {
              beforePathLabel: juteAfterPositionBeforePathLabel,
              afterPathLabel: juteAfterPositionAfterPathLabel
            },
            commercialHeightInMM: juteAfter.commercialHeightInMM,
            technicalHeightInPicks: juteAfter.technicalHeightInPicks
          },
          pattern
        },
        {emitEvent: false}
      );
    } else {
      this.juteForm.controls.hasJuteAfter.setValue(false);
    }

    this.syncJutePathHeights('juteBefore.commercialHeightInMM', 'juteBefore.technicalHeightInPicks');
    this.syncJutePathHeights('juteAfter.commercialHeightInMM', 'juteAfter.technicalHeightInPicks');
  }

  private syncJutePathHeights(commercialHeightFormFieldName: string, technicalHeightFormFieldName: string): void {
    FormValueHelper.syncValuesOfFormFieldsByFactor(
      this.juteForm.get(commercialHeightFormFieldName),
      this.juteForm.get(technicalHeightFormFieldName),
      this.factorForConversionFromMillimetersToPicks,
      this.factorForConversionFromPicksToMillimeters,
      this.machineQuality.weaveStructure.picksPerColorLine ?? null
    )
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe();
  }
}
